import {
  APARTMENT_ERROR_MESSAGES,
  SPACE_BATCH_MAX_ROWS,
  SPACE_COLUMNS,
  type SpaceBatchRequest,
  type SpaceBatchResponse,
  type SpaceField,
  type SpaceKind,
  spaceBatchResponseSchema,
} from '@sivan/shared';
import { askConfirm } from '../ui/confirm';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SortColumn } from 'react-data-grid';
import { SheetGrid, type SheetColumnDef, type SheetGridAdapter } from './SheetGrid';
import { SheetReview } from './SheetReview';
import { SpaceCards } from './SpaceCards';
import { SpaceForm } from './SpaceForm';
import { UndoButtons } from './UndoButtons';
import type { PastePermissions } from './apartment-paste';
import type { ClipboardTable } from './clipboard';
import { and, count } from './labels';
import { checkTargets } from './sheet-paste';
import {
  type SpaceContext,
  type SpaceRow,
  rowsFromSpaces,
  spaceDraft as draft,
  toSpaceBatchRows,
} from './space-draft';
import { useBatchReview } from './use-batch-review';
import { useHistory } from './use-history';
import type { SpaceSheetData } from './use-master-data';
import { useNarrowScreen } from './use-narrow-screen';
import { reportPendingChanges, warnOnUnload } from './unsaved-changes';

export const SPACE_NAMES: Readonly<
  Record<SpaceKind, { one: string; many: string; title: string }>
> = {
  parking: { one: 'חניה', many: 'חניות', title: 'חניות' },
  storage: { one: 'מחסן', many: 'מחסנים', title: 'מחסנים' },
};

const WIDTHS: Readonly<Record<SpaceField, number>> = {
  location: 120,
  level: 96,
  number: 120,
  building: 96,
  apartment: 96,
};

const columnsFor = (kind: SpaceKind): readonly SheetColumnDef[] =>
  SPACE_COLUMNS[kind].map((c) => ({
    ...c,
    // The space's own columns take the colour of its kind; the apartment columns their own.
    group: c.group === 'space' ? kind : 'apartment',
    available: true,
    width: WIDTHS[c.key],
    frozen: c.group === 'space',
  }));

export function spaceErrorText(kind: SpaceKind, field: string, code: string): string {
  const column = SPACE_COLUMNS[kind].find((c) => c.key === field);
  const message = APARTMENT_ERROR_MESSAGES[code] ?? 'ערך לא תקין';
  return column === undefined ? message : `${column.header}: ${message}`;
}

const isField = (key: string): key is SpaceField => draft.fields.some((f) => f === key);

interface Filters {
  readonly search: string;
  readonly location: string;
  readonly pendingOnly: boolean;
}

function compare(a: string, b: string): number {
  const numeric = /^-?\d+(\.\d+)?$/;
  if (numeric.test(a) && numeric.test(b)) return Number(a) - Number(b);
  return a.localeCompare(b, 'he', { numeric: true });
}

interface SpaceSheetProps {
  readonly projectId: string;
  readonly kind: SpaceKind;
  readonly data: SpaceSheetData;
  readonly permissions: PastePermissions;
  readonly onSaved: (message: string) => void;
  readonly savedMessage: string | null;
}

/**
 * The Parking or Storage sheet of one project (MD-2A): one row per physical space (location, level,
 * number) and the apartment it is allocated to. Spaces are created by typing or pasting new rows;
 * assignments, transfers and ends go through the same unified review and atomic save as the
 * Apartments sheet.
 */
export function SpaceSheet({
  projectId,
  kind,
  data,
  permissions,
  onSaved,
  savedMessage,
}: SpaceSheetProps) {
  const names = SPACE_NAMES[kind];
  const context: SpaceContext = useMemo(
    () => ({ kind, structures: data.structures }),
    [kind, data.structures],
  );
  const columns = useMemo(() => columnsFor(kind), [kind]);
  const narrow = useNarrowScreen();
  const history = useHistory<SpaceRow[]>(() => rowsFromSpaces(data.spaces));
  const rows = history.value;
  const [filters, setFilters] = useState<Filters>({ search: '', location: '', pendingOnly: false });
  const [sort, setSort] = useState<readonly SortColumn[]>([]);
  const [message, setMessage] = useState<{
    text: string;
    tone: 'info' | 'success' | 'error';
  } | null>(savedMessage === null ? null : { tone: 'success', text: savedMessage });
  const [editing, setEditing] = useState<SpaceRow | null>(null);

  const displayed = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const matches = (row: SpaceRow) =>
      draft.rowState(row) !== 'stored' ||
      (!filters.pendingOnly &&
        (search === '' ||
          draft.displayValue(row, 'number').toLowerCase().includes(search) ||
          draft.displayValue(row, 'apartment').toLowerCase().includes(search)) &&
        (filters.location === '' || draft.displayValue(row, 'location') === filters.location));
    const body = rows.filter((row) => draft.rowState(row) !== 'placeholder' && matches(row));
    const [first] = sort;
    if (first !== undefined) {
      const direction = first.direction === 'DESC' ? -1 : 1;
      body.sort(
        (a, b) =>
          direction *
          compare(draft.displayValue(a, first.columnKey), draft.displayValue(b, first.columnKey)),
      );
    }
    return [...body, ...rows.filter((row) => draft.rowState(row) === 'placeholder')];
  }, [rows, filters, sort]);

  const pending = draft.summarize(rows);
  const pendingTotal = pending.changed + pending.created;
  const hasPending = pendingTotal > 0;
  const locations = data.structures.filter(
    (s) => s.kind !== (kind === 'parking' ? 'storage' : 'parking'),
  );

  useEffect(() => {
    reportPendingChanges(pendingTotal);
  }, [pendingTotal]);
  useEffect(() => {
    window.addEventListener('beforeunload', warnOnUnload);
    return () => {
      window.removeEventListener('beforeunload', warnOnUnload);
      reportPendingChanges(0);
    };
  }, []);

  const allowed = useCallback(
    (row: SpaceRow | null, key: string) =>
      key === 'building' || key === 'apartment'
        ? permissions.allocate
        : (row?.unitId ?? null) === null
          ? permissions.create
          : permissions.edit,
    [permissions],
  );

  const adapter = useMemo(
    (): SheetGridAdapter<SpaceRow> => ({
      key: (row) => row.key,
      display: draft.displayValue,
      state: draft.rowState,
      errors: (row) => row.errors,
      isCleared: (row, key) => isField(key) && draft.isCleared(row, key),
      isChanged: (row, key) => draft.changedFields(row).some((f) => f === key),
      locked: (row, key) => (isField(key) ? draft.lockedReason(row, key) : null),
      base: (row, key) => (isField(key) ? (row.base?.[key] ?? '') : ''),
      canEdit: (row, key) =>
        isField(key) &&
        draft.lockedReason(row, key) === null &&
        allowed(row.unitId === null ? null : row, key),
      errorText: (key, code) => spaceErrorText(kind, key, code),
      clearedText: (key) =>
        key === 'building' || key === 'apartment' ? 'ההצמדה תסתיים בשמירה' : 'יימחק בשמירה',
    }),
    [allowed, kind],
  );

  const edit = (key: string, field: SpaceField, value: string) => {
    history.change((current) => {
      const { rows: next, refused } = draft.setCell(current, key, field, value, context);
      if (refused !== null)
        setMessage({
          tone: 'error',
          text:
            refused === 'not_clearable'
              ? 'לא ניתן לרוקן את המבנה או את המספר.'
              : (APARTMENT_ERROR_MESSAGES[refused] ?? 'לא ניתן לשנות שדה זה.'),
        });
      return next;
    });
  };

  const onPaste = (table: ClipboardTable, anchorIndex: number, anchorColumn: number) => {
    const problem = checkTargets(table, displayed, anchorIndex, anchorColumn, {
      columns,
      isNew: (row) => row === undefined || draft.rowState(row) === 'placeholder',
      locked: (row, key) => (isField(key) ? draft.lockedReason(row, key) : null),
      allowed,
    });
    if (problem !== null) {
      setMessage({ tone: 'error', text: problem });
      return;
    }
    const result = draft.applyPaste(rows, displayed, table, anchorIndex, anchorColumn, context);
    history.change(() => result.rows);
    const parts = [
      result.updatedRows > 0 ? count(result.updatedRows, 'עדכון אחד', 'עדכונים') : null,
      result.newRows > 0
        ? count(result.newRows, `${names.one} חדש/ה`, `${names.many} חדשים`)
        : null,
    ].filter((p): p is string => p !== null);
    const [a, b] = parts;
    setMessage({
      tone: 'info',
      text: `הודבקו ${count(table.length, 'שורה אחת', 'שורות')}: ${a === undefined ? 'ללא שינוי' : b === undefined ? a : and(a, b)}. תאים ריקים לא שינו ערכים קיימים. דבר לא נשמר עדיין.`,
    });
  };

  const flow = useBatchReview<SpaceBatchRequest, SpaceBatchResponse>({
    previewPath: `/projects/${projectId}/space-sheet/${kind}/batch/preview`,
    commitPath: `/projects/${projectId}/space-sheet/${kind}/batch`,
    schema: spaceBatchResponseSchema,
    annotate: (result) => {
      history.annotate((current) => draft.applyResult(current, result));
    },
    onSaved: (result) => {
      const s = result.summary;
      onSaved(
        `השינויים נשמרו: ${and(count(s.create, `${names.one} חדש/ה`, `${names.many} חדשים`), count(s.update, 'עדכון אחד', 'עדכונים'))}, ${and(count(s.assign, 'הצמדה חדשה אחת', 'הצמדות חדשות'), count(s.end, 'הצמדה אחת הסתיימה', 'הצמדות הסתיימו'))}.`,
      );
    },
    onError: (text) => {
      setMessage({ tone: 'error', text });
    },
  });

  const startReview = () => {
    const batchRows = toSpaceBatchRows(rows);
    if (batchRows.length > SPACE_BATCH_MAX_ROWS) {
      setMessage({
        tone: 'error',
        text: `ניתן לשמור עד ${String(SPACE_BATCH_MAX_ROWS)} שורות בפעם אחת. שמרו בחלקים.`,
      });
      return;
    }
    flow.start({ batchId: crypto.randomUUID(), rows: batchRows, confirmedEnds: [] });
  };

  const discardAll = async () => {
    if (
      !(await askConfirm({
        title:
          pendingTotal === 1
            ? 'לבטל את השינוי הממתין?'
            : `לבטל את כל ${String(pendingTotal)} השינויים הממתינים?`,
        confirmLabel: 'ביטול השינויים',
        danger: true,
      }))
    )
      return;
    history.reset(rowsFromSpaces(data.spaces));
    setMessage({ tone: 'info', text: 'השינויים הממתינים בוטלו.' });
  };

  const byKey = new Map(rows.map((row) => [row.key, row]));
  const titleOf = (row: SpaceRow) => {
    const level = draft.displayValue(row, 'level');
    return `${names.one} ${draft.displayValue(row, 'number') || '—'} · ${draft.displayValue(row, 'location') || '—'}${level === '' ? '' : ` · מרתף ${level}`}`;
  };
  const anyEdit = permissions.create || permissions.edit || permissions.allocate;

  return (
    <section className="workspace" aria-labelledby="space-sheet-title">
      <div className="toolbar">
        <h2 id="space-sheet-title" className="toolbar__title">
          {names.title}
        </h2>
        <label className="toolbar__field">
          <span>חיפוש</span>
          <input
            type="search"
            value={filters.search}
            placeholder={`מספר ${names.one} או דירה`}
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
            }}
          />
        </label>
        <label className="toolbar__field">
          <span>{kind === 'parking' ? 'חניון' : 'מבנה'}</span>
          <select
            value={filters.location}
            onChange={(e) => {
              setFilters({ ...filters, location: e.target.value });
            }}
          >
            <option value="">הכול</option>
            {locations.map((s) => (
              <option key={s.id} value={s.code}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
        </label>
        <label className="toolbar__toggle">
          <input
            type="checkbox"
            checked={filters.pendingOnly}
            onChange={(e) => {
              setFilters({ ...filters, pendingOnly: e.target.checked });
            }}
          />
          <span>שינויים ממתינים בלבד</span>
        </label>
        <div className="toolbar__actions">
          <p className="pending-count" aria-live="polite">
            {hasPending
              ? `ממתינים לשמירה: ${count(pending.changed, 'עדכון אחד', 'עדכונים')}, ${count(pending.created, 'חדש/ה אחד/ת', 'חדשים')}${pending.withErrors > 0 ? `, ${count(pending.withErrors, 'אחת עם שגיאה', 'עם שגיאות')}` : ''}`
              : 'אין שינויים ממתינים'}
          </p>
          {narrow && permissions.create && (
            <button
              type="button"
              className="button"
              onClick={() => {
                setEditing(draft.newRow());
              }}
            >
              הוספת {names.one}
            </button>
          )}
          <UndoButtons
            canUndo={history.canUndo}
            canRedo={history.canRedo}
            onUndo={history.undo}
            onRedo={history.redo}
          />
          <button
            type="button"
            className="button"
            disabled={!hasPending}
            onClick={() => void discardAll()}
          >
            ביטול שינויים
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={!hasPending}
            onClick={startReview}
          >
            בדיקה ושמירה
          </button>
        </div>
      </div>

      <div
        role="status"
        aria-live="polite"
        className={message === null ? 'visually-hidden' : `notice notice--${message.tone}`}
      >
        {message?.text}
      </div>
      {data.truncated && (
        <p className="notice notice--warning">מוצגים {data.spaces.length} הראשונים בלבד.</p>
      )}

      {narrow ? (
        <SpaceCards rows={displayed} kind={kind} canEdit={anyEdit} onEdit={setEditing} />
      ) : (
        <>
          <details className="paste-help">
            <summary>הדבקה מ-Excel: יצירת {names.many} והצמדה לדירות</summary>
            <p>
              כל שורה היא {names.one} אחד/ת: {kind === 'parking' ? 'חניון' : 'מבנה'}, מרתף ומספר.
              הדבקה בשורה החדשה שבסוף הגיליון יוצרת {names.many} חדשים, ושורות נוספות נוצרות
              אוטומטית. בעמודות "בנין" ו"דירה" מצמידים לדירה; לדירה יכולים להיות כמה. העברה מדירה
              אחרת או סיום הצמדה (Delete) דורשים אישור בבדיקה. תא ריק בהדבקה אינו מוחק דבר.
            </p>
            <p>
              Shift+לחיצה או Shift+חצים בוחרים טווח, Ctrl/Cmd+C מעתיק, Ctrl/Cmd+Z מבטל
              ו-Ctrl/Cmd+Shift+Z מבצע מחדש.
            </p>
          </details>
          <SheetGrid
            label={`גיליון ${names.many}`}
            rows={displayed}
            columns={columns}
            adapter={adapter}
            sortColumns={sort}
            onSortColumnsChange={setSort}
            onCellChange={(row, key, value) => {
              if (isField(key)) edit(row.key, key, value);
            }}
            onCellClear={(row, key) => {
              if (isField(key)) edit(row.key, key, '');
            }}
            onPaste={onPaste}
            onMessage={(text, tone) => {
              setMessage({ tone, text });
            }}
            onUndo={history.undo}
            onRedo={history.redo}
          />
        </>
      )}

      {editing !== null && (
        <SpaceForm
          key={editing.key}
          row={editing}
          kind={kind}
          allowed={allowed}
          onApply={(key, changes) => {
            history.change((current) => {
              let next = current.some((row) => row.key === key)
                ? current
                : [
                    ...current.filter((r) => draft.rowState(r) !== 'placeholder'),
                    { ...draft.newRow(), key },
                  ];
              for (const change of changes)
                next = draft.setCell(next, key, change.field, change.value, context).rows;
              return next;
            });
            setEditing(null);
          }}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}

      <SheetReview
        open={flow.review !== null}
        phase={flow.review?.phase ?? 'reviewing'}
        result={flow.review?.result ?? null}
        confirmed={flow.review?.confirmed ?? new Set()}
        perspective="space"
        labels={{
          create: [`${names.one} חדש/ה`, `${names.many} חדשים`],
          update: [`${names.one} מתעדכן/ת`, `${names.many} מתעדכנים`],
        }}
        errorText={(field, code) => spaceErrorText(kind, field, code)}
        describe={(key) => {
          const row = byKey.get(key);
          if (row === undefined) return undefined;
          const changes = draft
            .changedFields(row)
            .filter((f) => f !== 'building' && f !== 'apartment')
            .map((f) => {
              const label = SPACE_COLUMNS[kind].find((c) => c.key === f)?.header ?? f;
              const before = row.base?.[f] ?? '';
              if (draft.isCleared(row, f)) return `${label}: הערך "${before}" יימחק`;
              const after = draft.displayValue(row, f);
              return row.base === null || before === ''
                ? `${label}: ${after}`
                : `${label}: "${before}" ← "${after}"`;
            });
          return { title: titleOf(row), changes };
        }}
        onToggleConfirm={flow.toggle}
        onConfirm={flow.commit}
        onClose={flow.close}
        onKeepMine={(key) => {
          history.change((current) => draft.keepMyChange(current, key));
          flow.close();
        }}
        onDiscard={(key) => {
          history.change((current) => draft.discardRow(current, key));
          flow.close();
        }}
      />
    </section>
  );
}
